import { Grid, IconButton } from '@mui/material';
import { useRef } from 'react';
import moment from 'moment';
import React from 'react';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { theme } from '../../../../shared/theme';
import { trackEvent } from '../../../../app/Analytics';

export default function ShareReservation(params: any) {
    const reservationInfo = params?.getReservationDetails;
    const getIndividualAsset = params?.getExperienceDetails;

    const fullAddress = getIndividualAsset
        ? `${getIndividualAsset?.streetAddress} ${getIndividualAsset?.suburb} ${getIndividualAsset?.postcode} ${getIndividualAsset?.state} ${getIndividualAsset?.countryCode}`
        : 'This experience is no longer available to rebook.';

    const reportTemplateRef: any = useRef(null);

    const joinNonEmpty = (parts: Array<string | undefined>) =>
        parts
            .map((p) => String(p ?? '').trim())
            .filter(Boolean)
            .join(', ');

    const fullName = (u: any) =>
        joinNonEmpty([u?.firstName, u?.lastName]) || undefined;
    const attendeeName = (u: any) =>
        [u?.firstName, u?.lastName].filter(Boolean).join(' ').trim() ||
        u?.name ||
        u?.email ||
        undefined;

    const escapeHtml = (value: any) =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    const humanize = (value: any) =>
        String(value ?? '')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/\b\w/g, (c) => c.toUpperCase());

    const formatDollar = (value: any) => {
        if (value === undefined || value === null || value === '') return '';
        const numeric =
            typeof value === 'number'
                ? value
                : Number(String(value).replace(/[$,\s]/g, ''));
        if (!Number.isNaN(numeric)) return `$${numeric.toLocaleString()}`;
        return String(value).startsWith('$') ? String(value) : `$${value}`;
    };

    const toAmountNumber = (value: any) => {
        const numeric =
            typeof value === 'number'
                ? value
                : Number(String(value ?? '').replace(/[$,\s]/g, ''));
        return Number.isNaN(numeric) ? null : numeric;
    };

    const handleGeneratePdf = () => {
        trackEvent(
            'Reservations',
            'Share Reservation',
            `Share Reservation (PDF)`,
        );

        const showCheckTimes = !(
            reservationInfo?.bookerType === 'owner' &&
            reservationInfo?.onBehalf === false
        );

        const experienceName =
            reservationInfo?.module?.name || reservationInfo?.moduleName;
        const experienceType =
            reservationInfo?.module?.type ||
            reservationInfo?.bookingInfo?.moduleType;
        const location = joinNonEmpty([
            reservationInfo?.module?.streetAddress ||
                getIndividualAsset?.streetAddress,
            reservationInfo?.module?.suburb || getIndividualAsset?.suburb,
            reservationInfo?.module?.state || getIndividualAsset?.state,
            reservationInfo?.module?.countryCode ||
                getIndividualAsset?.countryCode,
        ]);
        const company =
            reservationInfo?.guest?.name ||
            reservationInfo?.company?.name ||
            reservationInfo?.bookingInfo?.companyName ||
            (reservationInfo?.bookingInfo?.visitType
                ? reservationInfo?.owner?.name
                    ? `Booked on behalf of ${reservationInfo?.owner?.name}`
                    : undefined
                : reservationInfo?.owner?.name
                  ? `Blocked by ${reservationInfo?.owner?.name}`
                  : undefined);
        const primaryGuest =
            fullName(reservationInfo?.booker) ||
            fullName(reservationInfo?.guestUser) ||
            reservationInfo?.bookingInfo?.name;
        const reservationMadeBy =
            fullName(reservationInfo?.createdBy) ||
            fullName(reservationInfo?.booker);
        const visitType = reservationInfo?.bookingInfo?.visitType
            ? String(reservationInfo.bookingInfo.visitType)
            : undefined;
        const guestsAttendingRaw =
            reservationInfo?.bookingInfo?.guests ??
            reservationInfo?.bookingInfo?.guestCount ??
            reservationInfo?.bookingInfo?.attendees ??
            (reservationInfo?.bookingInfo?.moduleType !== 'activity'
                ? (reservationInfo?.bookingInfo?.children ?? 0) +
                  (reservationInfo?.bookingInfo?.adults ?? 0)
                : undefined);
        const guestsAttending =
            guestsAttendingRaw !== undefined && guestsAttendingRaw !== null
                ? String(guestsAttendingRaw)
                : undefined;
        const staffAttending = Array.isArray(reservationInfo?.staffAttending)
            ? reservationInfo.staffAttending
                  .map((u: any) => attendeeName(u))
                  .filter(Boolean)
                  .join('\n')
            : undefined;
        const guestsAttendingNames = [
            ...(Array.isArray(reservationInfo?.externalAttendees)
                ? reservationInfo.externalAttendees
                : []),
            ...(Array.isArray(reservationInfo?.guestAttending)
                ? reservationInfo.guestAttending
                : []),
        ]
            .map((u: any) => attendeeName(u))
            .filter(Boolean)
            .join('\n');
        const massagesRequested = joinNonEmpty([
            reservationInfo?.bookingInfo?.massageNotes,
            reservationInfo?.bookingInfo?.massage,
            reservationInfo?.bookingInfo?.wellnessNotes,
        ]);
        const cateringRequested = joinNonEmpty([
            reservationInfo?.bookingInfo?.cateringNotes,
            reservationInfo?.bookingInfo?.catering,
        ]);
        const reservationNotes =
            reservationInfo?.bookingInfo?.notes || reservationInfo?.notes || '';

        const checkinDate = reservationInfo?.startDate
            ? moment(reservationInfo.startDate).format('DD/MM/YYYY')
            : '';
        const checkoutDate = reservationInfo?.endDate
            ? moment(reservationInfo.endDate).format('DD/MM/YYYY')
            : '';
        const checkinTime = showCheckTimes
            ? reservationInfo?.bookingInfo?.checkinTime
            : '';
        const checkoutTime = showCheckTimes
            ? reservationInfo?.bookingInfo?.checkoutTime
            : '';
        const checkinText = [checkinDate, checkinTime]
            .filter(Boolean)
            .join(' ');
        const checkoutText = [checkoutDate, checkoutTime]
            .filter(Boolean)
            .join(' ');

        const summaryRows = [
            {
                label: 'Reservation status',
                value: reservationInfo?.status
                    ? humanize(reservationInfo.status)
                    : undefined,
            },
            { label: 'Experience', value: experienceName },
            { label: 'Type', value: experienceType },
            { label: 'Location', value: location || fullAddress || undefined },
            { label: 'Company', value: company },
            { label: 'Primary guest', value: primaryGuest },
            { label: 'Reservation made by', value: reservationMadeBy },
            { label: 'Visit type', value: visitType },
            { label: 'Guests attending', value: guestsAttending },
            { label: 'Massages', value: massagesRequested || undefined },
            { label: 'Catering', value: cateringRequested || undefined },
            { label: 'Notes', value: reservationNotes || undefined },
            { label: 'Check-in', value: checkinText || undefined },
            { label: 'Check-out', value: checkoutText || undefined },
        ].filter((row) => row.value);

        const summaryHtml = summaryRows.length
            ? `<section class="summary">
                <h3>Reservation Summary</h3>
                <div class="summary-grid">
                    ${summaryRows
                        .map(
                            (row) =>
                                `<div class="summary-row"><span class="label">${row.label}:</span> <span class="value">${escapeHtml(row.value)}</span></div>`,
                        )
                        .join('')}
                </div>
            </section>`
            : '';

        const attendeeSectionHtml =
            staffAttending || guestsAttendingNames
                ? `<section class="summary">
                    <h3>Attendees</h3>
                    ${
                        staffAttending
                            ? `<div class="summary-row"><span class="label">Staff attending:</span><ul class="name-list">${staffAttending
                                  .split('\n')
                                  .filter(Boolean)
                                  .map(
                                      (name: string) =>
                                          `<li>${escapeHtml(name)}</li>`,
                                  )
                                  .join('')}</ul></div>`
                            : ''
                    }
                    ${
                        guestsAttendingNames
                            ? `<div class="summary-row"><span class="label">Guests attending:</span><ul class="name-list">${guestsAttendingNames
                                  .split('\n')
                                  .filter(Boolean)
                                  .map(
                                      (name: string) =>
                                          `<li>${escapeHtml(name)}</li>`,
                                  )
                                  .join('')}</ul></div>`
                            : ''
                    }
                </section>`
                : '';

        const itineraryItems = Array.isArray(reservationInfo?.itineraryItems)
            ? reservationInfo.itineraryItems
            : [];

        const groupItineraryByDay = () => {
            const start = reservationInfo?.startDate
                ? moment(reservationInfo.startDate)
                : null;
            const end = reservationInfo?.endDate
                ? moment(reservationInfo.endDate)
                : null;
            if (!start || !end || !start.isValid() || !end.isValid()) {
                return [];
            }

            const dayMap = new Map<string, any[]>();
            const cursor = start.clone().startOf('day');
            const endDay = end.clone().startOf('day');

            while (cursor.isSameOrBefore(endDay, 'day')) {
                dayMap.set(cursor.format('YYYY-MM-DD'), []);
                cursor.add(1, 'day');
            }

            itineraryItems.forEach((item: any) => {
                const itemStart = item?.startDate
                    ? moment(item.startDate).startOf('day')
                    : null;
                const itemEnd = item?.endDate
                    ? moment(item.endDate).startOf('day')
                    : itemStart;

                if (!itemStart || !itemStart.isValid()) return;
                const finalEnd =
                    itemEnd && itemEnd.isValid() ? itemEnd : itemStart;

                const d = itemStart.clone();
                while (d.isSameOrBefore(finalEnd, 'day')) {
                    const key = d.format('YYYY-MM-DD');
                    if (!dayMap.has(key)) {
                        dayMap.set(key, []);
                    }
                    dayMap.get(key)?.push(item);
                    d.add(1, 'day');
                }
            });

            return Array.from(dayMap.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([date, items]) => ({
                    date,
                    items,
                }));
        };

        const dayGroups = groupItineraryByDay();
        const itineraryHtml = dayGroups.length
            ? `<section class="summary">
                <h3>Reservation Itinerary</h3>
                ${dayGroups
                    .map((group) => {
                        const dayLabel = moment(group.date).format(
                            'ddd D MMM YYYY',
                        );
                        const rows =
                            group.items.length === 0
                                ? `<li class="muted">No plans for this day</li>`
                                : group.items
                                      .map((item: any) => {
                                          const type =
                                              item?.itineraryItemInfo
                                                  ?.itemType || 'Item';
                                          const title =
                                              item?.itineraryItemInfo?.title ||
                                              item?.itineraryItemInfo
                                                  ?.restaurantName ||
                                              item?.itineraryItemInfo
                                                  ?.provider ||
                                              item?.itineraryItemInfo
                                                  ?.airline ||
                                              item?.moduleName ||
                                              'Itinerary item';
                                          const time = `${item?.startTime || ''}${item?.startTime && item?.endTime ? ' - ' : ''}${item?.endTime || ''}`;
                                          const statusRaw =
                                              item?.status ||
                                              item?.itineraryItemInfo?.status ||
                                              item?.itineraryItemInfo
                                                  ?.bookingStatus ||
                                              undefined;
                                          const statusText = statusRaw
                                              ? String(statusRaw)
                                                    .replace(/_/g, ' ')
                                                    .replace(/\b\w/g, (c) =>
                                                        c.toUpperCase(),
                                                    )
                                              : '';
                                          return `<li><strong>${type}</strong> - ${title}${time ? ` <span class="muted">(${time})</span>` : ''}${statusText ? ` <span class="status">[${statusText}]</span>` : ''}</li>`;
                                      })
                                      .join('');
                        return `<section class="itinerary-day"><h4>${dayLabel}</h4><ul>${rows}</ul></section>`;
                    })
                    .join('')}
            </section>`
            : '';

        const heroImage =
            reservationInfo?.module?.photo ||
            reservationInfo?.module?.image?.src ||
            reservationInfo?.module?.image?.url ||
            reservationInfo?.module?.coverImage?.src ||
            reservationInfo?.module?.coverImage?.url ||
            getIndividualAsset?.photos?.[0]?.src ||
            getIndividualAsset?.photo ||
            '';

        const imageHtml = heroImage
            ? `<section class="hero"><img src="${escapeHtml(
                  heroImage,
              )}" alt="Reservation experience" /></section>`
            : '';

        const propertyDescription =
            reservationInfo?.module?.description ||
            getIndividualAsset?.description ||
            '';

        const amenityExcludeKeys = new Set([
            'desks',
            'privateOffices',
            'meetingRooms',
            'basePrice',
            'beds',
            'bedrooms',
            'baths',
            'bathrooms',
            'overnightGuests',
            'maxGuests',
            'rules',
            'subType',
            'dropoffStreetAddress',
            'dropoffSuburb',
            'dropoffPostcode',
            'dropoffState',
            'dropoffCountryCode',
        ]);
        const detailsObj =
            reservationInfo?.module?.details ||
            getIndividualAsset?.details ||
            {};
        const amenityNames = Object.entries(detailsObj)
            .filter(
                ([key, value]) =>
                    !amenityExcludeKeys.has(key) &&
                    (value === true ||
                        (typeof value === 'number' && value > 0) ||
                        (Array.isArray(value) && value.length > 0)),
            )
            .map(([key]) => humanize(key));

        const aboutHtml =
            propertyDescription || amenityNames.length
                ? `<section class="summary">
                    <h3>About this Property</h3>
                    ${
                        propertyDescription
                            ? `<p class="description">${escapeHtml(propertyDescription)}</p>`
                            : ''
                    }
                    ${
                        amenityNames.length
                            ? `<div class="amenities-wrap"><span class="label">Amenities:</span><div class="chips">${amenityNames
                                  .map(
                                      (a) =>
                                          `<span class="chip">${escapeHtml(a)}</span>`,
                                  )
                                  .join('')}</div></div>`
                            : ''
                    }
                </section>`
                : '';

        const reservationChargesSource = [
            ...(Array.isArray(reservationInfo?.charges)
                ? reservationInfo.charges
                : []),
            ...(Array.isArray(reservationInfo?.bookingCharges)
                ? reservationInfo.bookingCharges
                : []),
        ].filter(
            (c: any) =>
                c?.isExpense !== true &&
                !['expense', 'expenseClaim', 'claim'].includes(
                    String(c?.chargeType || c?.type || '').toLowerCase(),
                ),
        );

        const additionalExpensesSource = [
            ...(Array.isArray(reservationInfo?.expenseClaims)
                ? reservationInfo.expenseClaims
                : []),
            ...(Array.isArray(reservationInfo?.bookingCharges)
                ? reservationInfo.bookingCharges.filter((c: any) =>
                      ['expense', 'expenseclaim', 'claim'].includes(
                          String(c?.chargeType || c?.type || '').toLowerCase(),
                      ),
                  )
                : []),
        ];

        const toChargeRow = (c: any) => {
            const name =
                c?.name || c?.chargeName || c?.type || c?.label || 'Charge';
            const cleanName = humanize(name)
                .replace(/\bPer\s+(Night|Day|Hour|Guest|Week)\b/gi, '')
                .replace(/\s{2,}/g, ' ')
                .trim();
            const amount =
                c?.amount ?? c?.total ?? c?.price ?? c?.unitPrice ?? c?.value;
            const status = c?.status ? ` (${c.status})` : '';
            const multiplierText = Array.isArray(c?.feeMultipliers)
                ? c.feeMultipliers
                      .map((m: any) => {
                          const n = Number(m?.value ?? 0);
                          const unit = String(m?.type ?? '')
                              .toLowerCase()
                              .replace(/s$/, '');
                          if (!n || !unit) return '';
                          return `${n} ${unit}${n > 1 ? 's' : ''}`;
                      })
                      .filter(Boolean)
                      .join(' x ')
                : '';
            const label = multiplierText
                ? `${cleanName} x ${multiplierText}`
                : cleanName;
            return amount !== undefined && amount !== null && amount !== ''
                ? `<li><strong>${escapeHtml(label)}</strong>: ${escapeHtml(
                      formatDollar(amount),
                  )}${escapeHtml(status)}</li>`
                : `<li><strong>${escapeHtml(label)}</strong>${escapeHtml(
                      status,
                  )}</li>`;
        };

        const reservationChargesRows = reservationChargesSource
            .map((c: any) => {
                return toChargeRow(c);
            })
            .join('');

        const reservationChargesTotal = reservationChargesSource.reduce(
            (sum: number, c: any) => {
                const n = toAmountNumber(
                    c?.amount ??
                        c?.total ??
                        c?.price ??
                        c?.unitPrice ??
                        c?.value,
                );
                return n === null ? sum : sum + n;
            },
            0,
        );

        const additionalExpensesRows = additionalExpensesSource
            .map((c: any) => toChargeRow(c))
            .join('');

        const additionalExpensesTotal = additionalExpensesSource.reduce(
            (sum: number, c: any) => {
                const n = toAmountNumber(
                    c?.amount ??
                        c?.total ??
                        c?.price ??
                        c?.unitPrice ??
                        c?.value,
                );
                return n === null ? sum : sum + n;
            },
            0,
        );

        const bookingTotal = reservationInfo?.bookingTotal || {};
        const bookingSubTotal = bookingTotal?.subTotal;
        const bookingDiscount = bookingTotal?.discount;
        const bookingServiceFee = bookingTotal?.guestServiceFee;
        const bookingServiceFeePct = bookingTotal?.guestServiceFeePercentage;
        const bookingGst = bookingTotal?.gst;
        const bookingTotalInc = bookingTotal?.total;
        const grandTotal =
            (toAmountNumber(bookingTotalInc) || 0) +
            (toAmountNumber(additionalExpensesTotal) || 0);

        const bookingTotalsHtml =
            bookingSubTotal !== undefined ||
            bookingServiceFee !== undefined ||
            bookingGst !== undefined ||
            bookingTotalInc !== undefined
                ? `<div class="totals-block">
                    ${
                        bookingSubTotal !== undefined
                            ? `<div class="price-row"><span>Subtotal</span><strong>${escapeHtml(
                                  formatDollar(bookingSubTotal),
                              )}</strong></div>`
                            : ''
                    }
                    ${
                        bookingDiscount
                            ? `<div class="price-row"><span>Discount</span><strong>- ${escapeHtml(
                                  formatDollar(bookingDiscount),
                              )}</strong></div>`
                            : ''
                    }
                    ${
                        bookingServiceFee !== undefined &&
                        bookingServiceFee !== null
                            ? `<div class="price-row"><span>7DAY Service Fee ${
                                  bookingServiceFeePct ?? ''
                              }%</span><strong>${escapeHtml(
                                  formatDollar(bookingServiceFee),
                              )}</strong></div>`
                            : ''
                    }
                    ${
                        bookingGst !== undefined
                            ? `<div class="price-row"><span>GST (10%)</span><strong>${escapeHtml(
                                  formatDollar(bookingGst),
                              )}</strong></div>`
                            : ''
                    }
                    ${
                        bookingTotalInc !== undefined
                            ? `<div class="price-row total"><span>Booking Total (inc GST)</span><strong>${escapeHtml(
                                  formatDollar(bookingTotalInc),
                              )}</strong></div>`
                            : ''
                    }
                </div>`
                : '';

        const chargesHtml =
            reservationChargesRows ||
            additionalExpensesRows ||
            bookingTotalsHtml
                ? `<section class="summary">
                    <h3>Charges</h3>
                    <h4>Booking Expenses Incurred</h4>
                    ${
                        reservationChargesRows
                            ? `<ul>${reservationChargesRows}</ul>`
                            : `<p class="muted-block">No booking expenses found.</p>`
                    }
                    ${bookingTotalsHtml}
                    <h4>Additional Expenses Incurred</h4>
                    ${
                        additionalExpensesRows
                            ? `<ul>${additionalExpensesRows}</ul><div class="total-row"><strong>Total Additional Expenses (inc GST):</strong> ${escapeHtml(
                                  formatDollar(additionalExpensesTotal),
                              )}</div>`
                            : `<p class="muted-block">No additional expenses have been applied.</p>`
                    }
                    <h4>Total Reservation Charges</h4>
                    <div class="totals-block">
                        <div class="price-row"><span>Booking Total (inc GST)</span><strong>${escapeHtml(
                            formatDollar(
                                bookingTotalInc ?? reservationChargesTotal,
                            ),
                        )}</strong></div>
                        <div class="price-row"><span>Total Additional Expenses (inc GST)</span><strong>${escapeHtml(
                            formatDollar(additionalExpensesTotal),
                        )}</strong></div>
                        <div class="price-row total"><span>Total (Booking + Additional)</span><strong>${escapeHtml(
                            formatDollar(grandTotal),
                        )}</strong></div>
                    </div>
                </section>`
                : '';

        const paymentSource =
            (Array.isArray(reservationInfo?.guestPaymentHistory) &&
                reservationInfo?.guestPaymentHistory) ||
            (Array.isArray(reservationInfo?.paymentHistory) &&
                reservationInfo?.paymentHistory) ||
            (Array.isArray(reservationInfo?.payments) &&
                reservationInfo?.payments) ||
            (Array.isArray(reservationInfo?.transactions) &&
                reservationInfo?.transactions) ||
            [];

        const paymentRows = paymentSource
            .map((p: any) => {
                const dateRaw =
                    p?.createdAt || p?.date || p?.paidAt || p?.updatedAt;
                const dateText =
                    dateRaw && moment(dateRaw).isValid()
                        ? moment(dateRaw).format('DD/MM/YYYY')
                        : dateRaw || '';
                const method = p?.method || p?.paymentMethod || '';
                const status = p?.status || '';
                const amount =
                    p?.amount ??
                    p?.total ??
                    p?.value ??
                    p?.guestPayment ??
                    p?.paidAmount ??
                    '';
                const parts = [
                    dateText && `Date: ${dateText}`,
                    method && `Method: ${humanize(method)}`,
                    status && `Status: ${humanize(status)}`,
                    amount !== '' && `Amount: ${formatDollar(amount)}`,
                ].filter(Boolean);
                return parts.length > 0
                    ? `<li>${escapeHtml(parts.join(' | '))}</li>`
                    : '';
            })
            .filter(Boolean)
            .join('');

        const paymentHtml = paymentRows
            ? `<section class="summary"><h3>Payments</h3><ul>${paymentRows}</ul></section>`
            : '';

        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        const frameWindow = printFrame.contentWindow;
        if (!frameDoc || !frameWindow) {
            printFrame.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(`
            <html>
            <head>
                <title>Reservation Details</title>
                <style>
                    body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #1f3550; }
                    h1 { margin: 0 0 8px 0; font-size: 24px; }
                    h2 { margin: 0 0 24px 0; font-size: 14px; font-weight: 500; color: #5f7489; }
                    h3 { margin: 18px 0 8px 0; font-size: 15px; }
                    h4 { margin: 14px 0 8px 0; font-size: 14px; }
                    .hero { margin: 0 0 14px 0; border: 1px solid #d7e0e8; border-radius: 10px; overflow: hidden; }
                    .hero img { width: 100%; max-height: 280px; object-fit: cover; display: block; }
                    ul { margin: 0 0 8px 0; padding-left: 18px; }
                    li { margin: 6px 0; line-height: 1.4; }
                    .muted { color: #6f8397; font-style: italic; }
                    .status { color: #35506b; font-weight: 700; }
                    .summary { margin: 0 0 18px 0; padding: 12px 14px; border: 1px solid #d7e0e8; border-radius: 8px; }
                    .summary h3 { margin-top: 0; }
                    .summary-grid { display: grid; grid-template-columns: 1fr; gap: 6px; }
                    .summary-row .label { font-weight: 700; }
                    .name-list { margin: 4px 0 0 0; padding-left: 18px; }
                    .name-list li { margin: 2px 0; }
                    .description { margin: 0 0 10px 0; line-height: 1.45; white-space: pre-wrap; }
                    .amenities-wrap { margin-top: 4px; }
                    .chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
                    .chip { display: inline-flex; align-items: center; border: 1px solid #d7e0e8; border-radius: 999px; padding: 4px 10px; font-size: 12px; }
                    .itinerary-day + .itinerary-day { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #d7e0e8; }
                    .total-row { margin: 6px 0 10px 0; font-size: 13px; }
                    .totals-block { margin: 8px 0 12px 0; border-top: 1px solid #d7e0e8; padding-top: 8px; }
                    .price-row { display: flex; justify-content: space-between; gap: 12px; margin: 4px 0; line-height: 1.4; }
                    .price-row.total { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #d7e0e8; font-weight: 800; }
                    .muted-block { margin: 0 0 10px 0; color: #6f8397; font-style: italic; }
                </style>
            </head>
            <body>
                <h1>Reservation Details</h1>
                <h2>#${reservationInfo?.id ?? ''}</h2>
                ${imageHtml}
                ${aboutHtml}
                ${summaryHtml}
                ${attendeeSectionHtml}
                ${chargesHtml}
                ${paymentHtml}
                ${itineraryHtml}
            </body>
            </html>
        `);
        frameDoc.close();

        const cleanup = () => {
            setTimeout(() => {
                printFrame.remove();
            }, 0);
        };

        frameWindow.onafterprint = cleanup;
        setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();
        }, 150);

        // Fallback cleanup in case onafterprint doesn't fire.
        setTimeout(cleanup, 2000);
    };

    return (
        <Grid
            ref={reportTemplateRef}
            display={'block'}
            sx={{
                width: '100%',
            }}
        >
            <IconButton
                disableRipple
                onClick={handleGeneratePdf}
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    color: theme.palette.text.secondary,
                    opacity: 0.85,
                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        color: theme.palette.text.primary,
                        opacity: 1,
                    },
                }}
            >
                <DownloadOutlinedIcon fontSize='small' />
            </IconButton>
        </Grid>
    );
}
